import * as React from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { useAgencyId } from '@/data/rpc';
import { userChannel } from '@/lib/channels';
import { toNotification, type Notification } from '@/lib/notifications';
import { isActivityHint, pollDelay } from '@/lib/realtime';
import { useRealtime } from '@/realtime/RealtimeProvider';

/**
 * The bell's feed (change spec 2026-09-24/topbar, design 2): `notifications_feed` for the CURRENT
 * agency. The feed is computed on read from chat threads, tasks and load events, so there is no
 * notification stream to subscribe to. It is re-read:
 *   * on any publication on `user:<self>` (the chat hints, the rejection notice, task.changed: every
 *     one of them can change the feed), debounced; except the team activity hint (activity.new), which
 *     is the Activity tab's;
 *   * on every (re)subscribe of that channel (a first subscription recovers nothing);
 *   * by the fallback poll (120 s live, 20 s otherwise, none while the tab is hidden) and when the tab
 *     comes back. Deliveries and PODs have no user-channel hint, so they arrive by the poll.
 *
 * `markRead(until)` moves the server watermark to the newest notice the panel showed. The count drops
 * to zero at once; the feed is re-read after the server answers.
 */

export const FEED_LIMIT = 30;
const HINT_DEBOUNCE_MS = 300;

export interface NotificationsState {
  status: 'loading' | 'ready' | 'error';
  items: Notification[];
  unreadCount: number;
  reload(): void;
  markRead(untilIso: string): Promise<void>;
}

function isVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

export function useNotifications(): NotificationsState {
  const { client, user } = useAuth();
  const agencyId = useAgencyId();
  const { state: rtState, subscribe } = useRealtime();
  const userId = user?.id ?? null;

  const [status, setStatus] = React.useState<NotificationsState['status']>('loading');
  const [items, setItems] = React.useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const seq = React.useRef(0);

  const read = React.useCallback(async () => {
    if (!agencyId) return;
    const mine = ++seq.current;
    const { data, error } = await client.rpc('notifications_feed', { p_agency: agencyId, p_limit: FEED_LIMIT });
    if (mine !== seq.current) return;
    if (error) {
      console.warn('useNotifications: notifications_feed failed', error);
      setStatus((s) => (s === 'ready' ? s : 'error'));
      return;
    }
    const rows = (data ?? []) as Record<string, unknown>[];
    setItems(rows.map(toNotification).filter((n): n is Notification => n !== null));
    setUnreadCount(rows.length ? Number(rows[0].unread_count ?? 0) : 0);
    setStatus('ready');
  }, [client, agencyId]);

  const readRef = React.useRef(read);
  readRef.current = read;

  const debounce = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const reload = React.useCallback(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      debounce.current = null;
      void readRef.current();
    }, HINT_DEBOUNCE_MS);
  }, []);
  React.useEffect(
    () => () => {
      if (debounce.current) clearTimeout(debounce.current);
    },
    [],
  );

  // A new agency starts from nothing: another agency's notices never stand in for this one's.
  React.useEffect(() => {
    setItems([]);
    setUnreadCount(0);
    setStatus('loading');
    void read();
  }, [read]);

  React.useEffect(() => {
    if (!userId) return;
    return subscribe(userChannel(userId), {
      // A team activity hint (activity-feed) changes nothing in this feed: the Activity tab reads on it.
      onPublication: (data) => {
        if (isActivityHint(data)) return;
        reload();
      },
      onSubscribed: () => reload(),
      onError: () => console.warn('realtime: user channel refused or failed (notifications)'),
    });
  }, [userId, subscribe, reload]);

  const [visible, setVisible] = React.useState(isVisible);
  React.useEffect(() => {
    const onChange = () => {
      const now = isVisible();
      setVisible(now);
      if (now) reload();
    };
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, [reload]);
  const delay = agencyId ? pollDelay(rtState, visible) : null;
  React.useEffect(() => {
    if (delay === null) return;
    const id = setInterval(() => void readRef.current(), delay);
    return () => clearInterval(id);
  }, [delay]);

  const markRead = React.useCallback(
    async (untilIso: string) => {
      if (!agencyId) return;
      setUnreadCount(0);
      const { error } = await client.rpc('notifications_mark_read', { p_agency: agencyId, p_until: untilIso });
      if (error) console.warn('useNotifications: notifications_mark_read failed', error);
      reload();
    },
    [client, agencyId, reload],
  );

  return { status, items, unreadCount, reload, markRead };
}
